import * as Sentry from "@sentry/vue";

import {
  GetProjectPermissionsDocument,
  type GetProjectPermissionsQuery,
  type GetProjectPermissionsQueryVariables,
} from "@/graphql";
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { Permission } from "./PermissionsTypes";

type RoleLookupRow = { id: string; permissions: string[] };

/**
 * Map a project role id onto the permission strings that role grants.
 *
 * The lookup is case-insensitive on purpose: Hasura's `project_membership_role` computed field
 * falls back to the literal `accessAllProjects` for a tenant-wide privileged user who holds no
 * membership row on the project, while the seeded row in `roles` is `AccessAllProjects`.
 *
 * A role with no matching row (or no role at all — the project is invisible to this user)
 * resolves to no permissions, so every caller's `permissions.includes(...)` check fails closed.
 */
export const resolvePermissionsForRole = (role: string | null | undefined, roles: RoleLookupRow[]): Permission[] => {
  if (!role) return [];
  const matchedRole = roles.find((roleRow) => roleRow.id.toLowerCase() === role.toLowerCase());
  return matchedRole?.permissions ?? [];
};

export const getProjectPermissions = async (projectId: string): Promise<Permission[]> => {
  if (!projectId) return [];

  try {
    const result = await gaiupApolloClient.query<GetProjectPermissionsQuery, GetProjectPermissionsQueryVariables>({
      query: GetProjectPermissionsDocument,
      fetchPolicy: "network-only",
      variables: { projectId },
    });

    return resolvePermissionsForRole(result.data?.project_by_pk?.role, result.data?.roles ?? []);
  } catch (error) {
    Sentry.captureException(error);
    return [];
  }
};

export default getProjectPermissions;
